import {
    CardGrid,
    ContentSection,
    LinkRow,
    PageFrame,
    PageHero,
    TruthStateGrid,
} from '../components/PageBlocks';
import { CLAIM_TRUTH_CLASSES, getPageConfig } from '../data/page-config.mjs';
import { ROADMAP_TARGET } from '../data/site.mjs';

const page = getPageConfig('/about');

const AboutPage = () => {
    return (
        <PageFrame>
            <PageHero
                sectionId="overview"
                variant="manifesto"
                numeral="VIII"
                eyebrow="Company"
                title={page.h1}
                intro="Unlike cloud AI tools, Aven Studios is not building for the use case where routing does not matter. The company exists for the use case where it does — and where architecture, not policy, is the only credible answer."
                primaryCta={{ label: 'Contact', to: '/contact' }}
                secondaryCta={{ label: 'Reserve Position', to: '/contact', variant: 'secondary' }}
                proofStrip={[
                    'Founder-led. Architecturally grounded.',
                    'Control and privacy as structural design — not product features.',
                    'Early stage. Real foundation.',
                ]}
            />

            <ContentSection
                sectionId="thesis"
                eyebrow="Thesis"
                title="The current AI paradigm is structurally misaligned with serious private work."
                intro="Trust cannot be a promise. It must be enforced by architecture."
                borderTop={false}
            >
                <CardGrid
                    pattern="meta"
                    columns={2}
                    items={[
                        {
                            title: 'Cloud AI is structurally misaligned with private work',
                            body: 'The more capable AI becomes, the more private context it requires. Routing that context through cloud infrastructure by default is not a technical requirement — it is a convenience assumption that transfers control away from the user.',
                        },
                        {
                            title: 'Control is the category',
                            body: 'The next durable shift in computing is not a more capable assistant. It is sovereign control over where intelligence runs, what it is allowed to access, and what authority the user retains over their own systems.',
                        },
                    ]}
                />
            </ContentSection>

            <ContentSection
                sectionId="product-rationale"
                eyebrow="The Product Rationale"
                title="The problem is not capability. The problem is control architecture."
                intro="AvenBox is the product expression of the company thesis: capable AI that keeps intelligence under user authority — without routing private work through cloud systems by design."
            >
                <CardGrid
                    pattern="meta"
                    columns={2}
                    items={[
                        {
                            title: 'Privacy requires continuity',
                            body: 'The problem is not just where intelligence starts. It is whether control survives when work moves between direct device use, desk workflows, and other trusted environments.',
                        },
                        {
                            title: 'Portability requires a control model',
                            body: 'Portable private AI only matters if it stays coherent under real conditions. That requires routing discipline, defined trust boundaries, and a real system model — not just a capable language model.',
                        },
                    ]}
                />
            </ContentSection>

            <ContentSection
                sectionId="team"
                eyebrow="Who Is Building It"
                title="Founder-led. Built directly against the hardest problems."
                intro="Aven Studios is founder-led, focused on the core problems AvenBox is solving: control architecture and trust enforcement."
            >
                <div className="ed-team">
                    <article className="ed-team__card">
                        <div className="ed-team__photo">
                            <img src="/marko-vodopivec.jpg" alt="Marko Vodopivec" loading="lazy" />
                            <span className="ed-team__num">01</span>
                        </div>
                        <div className="ed-team__body">
                            <span className="ed-team__role">Founder</span>
                            <h3 className="ed-team__name">Marko Vodopivec</h3>
                            <p className="ed-team__quote">
                                <em>&ldquo;Cloud AI is structurally misaligned with private work. The architecture worth building is the control layer that gives users explicit authority over where their intelligence runs.&rdquo;</em>
                            </p>
                            <p className="ed-team__bio">
                                Directing product vision, hardware engineering, and the orchestration framework that makes AvenBox a system rather than a device.
                            </p>
                        </div>
                    </article>
                </div>
            </ContentSection>

            <ContentSection
                sectionId="principles"
                eyebrow="Principles"
                title="What Aven Studios stands for."
            >
                <CardGrid
                    pattern="meta"
                    items={[
                        {
                            title: 'Privacy by architecture',
                            body: 'Privacy is not a feature on AvenBox. It is a design requirement enforced by how the system routes, executes, and constrains access. The architecture decides where the boundary sits — not a settings page.',
                        },
                        {
                            title: 'Control over routing',
                            body: 'The point of AvenBox is explicit user authority over routing — where intelligence runs, what extends outward, and which external paths are accessed. That authority is a system guarantee, not a privacy preference.',
                        },
                        {
                            title: 'Seriousness over spectacle',
                            body: 'Aven Studios earns trust through clarity, proof discipline, and grounded engineering. That means publishing what is not yet real alongside what is — and treating the distinction as a feature, not a liability.',
                        },
                    ]}
                />
            </ContentSection>

            <ContentSection
                sectionId="current-stage"
                eyebrow="Current Stage"
                title="Early. Real. Disciplined."
                intro="Aven Studios applies the same claim discipline to itself that it applies to the product. Real now, prototype now, and roadmap are not marketing stages — they are how the company operates."
            >
                <TruthStateGrid
                    items={[
                        {
                            truthClass: CLAIM_TRUTH_CLASSES.REAL_NOW,
                            title: 'Clear thesis and real technical foundation',
                            description:
                                'The category position is defined. The technical foundation — local AI backend, privacy architecture, and orchestration core — exists and is under active development.',
                        },
                        {
                            truthClass: CLAIM_TRUTH_CLASSES.PROTOTYPE_NOW,
                            title: 'Working prototype stage',
                            description:
                                'AvenBox is a serious working prototype being shaped into a production-grade product. It is not yet a finished consumer platform.',
                        },
                        {
                            truthClass: CLAIM_TRUTH_CLASSES.ROADMAP,
                            title: `Launch targeting ${ROADMAP_TARGET}`,
                            description:
                                'Final platform maturity, later scale, and broader ecosystem deployment belong to roadmap planning rather than present-tense certainty.',
                        },
                    ]}
                />
            </ContentSection>

            <ContentSection sectionId="continue" eyebrow="Continue" title="The architecture and the commercial model.">
                <LinkRow
                    links={[
                        { label: 'Technology', to: '/technology' },
                        { label: 'Security & Privacy', to: '/security' },
                        { label: 'Pricing', to: '/pricing' },
                        { label: 'Contact', to: '/contact' },
                    ]}
                />
            </ContentSection>
        </PageFrame>
    );
};

export default AboutPage;
